import type { FastifyRequest, FastifyReply } from 'fastify'
import { routeOptimizationService } from './routeOptimization.service.js'
import type { OptimizeRouteInput, CompareProfilesInput } from './routeOptimization.schemas.js'
import { PlannerRepository } from '../planner/planner.repository.js'
import { createLogger } from '../../common/utils/logger.js'
const logger = createLogger('RouteOptimizationController')
import type { TSPResponse, CompareProfilesResponse } from '@my-roadtrip/types'

export class RouteOptimizationController {
  constructor(private readonly plannerRepository: PlannerRepository) {}

  /**
   * POST /api/route-optimization/plans/:planId/optimize
   * Optimize stage order using TSP algorithm
   */
  async optimizeRoute(
    request: FastifyRequest<{
      Params: { planId: string }
      Body: OptimizeRouteInput
    }>,
    reply: FastifyReply
  ): Promise<void> {
    const { planId } = request.params
    const { stageIds, constraints } = request.body
    const userId = request.user!.uid

    // Verify plan ownership
    const plan = await this.plannerRepository.findById(planId)
    if (!plan?.userId || plan.userId !== userId) {
      return reply.status(404).send({ error: 'Plan not found' })
    }

    // Get stages
    const stages = await this.plannerRepository.findStagesByIds(stageIds)
    if (stages.length !== stageIds.length) {
      return reply.status(400).send({ error: 'Some stages not found' })
    }

    // Validate stages have locations
    logger.debug('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━')
    logger.debug('📍 STAGES RECEIVED FOR OPTIMIZATION')
    logger.debug('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━')
    for (const [index, stage] of stages.entries()) {
      const coords = stage.location?.coordinates
      const coordsStr = coords ? `${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)}` : '❌ MISSING'
      logger.debug(`${index + 1}. ${stage.name || stage.id}`)
      logger.debug(`   Location: ${stage.location?.address || 'NO ADDRESS'}`)
      logger.debug(`   Coordinates: ${coordsStr}`)
      if (!coords?.lat || !coords?.lng) {
        logger.warn('   ⚠️  WARNING: Missing or invalid coordinates!')
      }
    }
    logger.debug('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━')

    const invalidStages = stages.filter(s => !s.location?.coordinates?.lat || !s.location?.coordinates?.lng)
    if (invalidStages.length > 0) {
      logger.error(`❌ ${invalidStages.length} stage(s) missing coordinates:`, {
        invalidStages: invalidStages.map(s => s.name || s.id)
      })
      return reply.status(400).send({ 
        error: 'Some stages are missing location coordinates',
        invalidStages: invalidStages.map(s => ({ id: s.id, name: s.name }))
      })
    }

    // Run TSP optimization
    const result = await routeOptimizationService.optimizeRoute(stages, constraints)

    // Get POIs for detour recommendations (from AI suggestions cache)
    // Integration with AI service for cached POIs will be done in Phase 2
    const pois: Array<{ id: string; name: string; category: string; lat: number; lng: number }> = []

    const detourRecommendations = await routeOptimizationService.suggestDetours(
      stages,
      pois,
      constraints.maxDetourKm,
      constraints.maxDetourPercent
    )

    const response: TSPResponse = {
      planId,
      result,
      detourRecommendations,
      computationTimeMs: 0, // Computation time will be tracked in future iteration
    }

    reply.send(response)
  }

  /**
   * POST /api/route-optimization/plans/:planId/compare-profiles
   * Compare different driving profiles
   */
  async compareProfiles(
    request: FastifyRequest<{
      Params: { planId: string }
      Body: CompareProfilesInput
    }>,
    reply: FastifyReply
  ): Promise<void> {
    const { planId } = request.params
    const { stageIds, profiles } = request.body
    const userId = request.user!.uid

    // Verify plan ownership
    const plan = await this.plannerRepository.findById(planId)
    if (!plan?.userId || plan.userId !== userId) {
      return reply.status(404).send({ error: 'Plan not found' })
    }

    // Get stages
    const stages = await this.plannerRepository.findStagesByIds(stageIds)
    if (stages.length !== stageIds.length) {
      return reply.status(400).send({ error: 'Some stages not found' })
    }

    // Compare profiles
    const comparisons = await routeOptimizationService.compareProfiles(stages, profiles, {
      profile: 'balanced', // Default baseline
    })

    const response: CompareProfilesResponse = {
      comparisons,
    }

    reply.send(response)
  }

  /**
   * GET /api/route-optimization/plans/:planId/detours
   * Suggest intelligent detours based on nearby POIs
   */
  async suggestDetours(
    request: FastifyRequest<{
      Params: { planId: string }
      Querystring: { maxDetourKm?: string; maxDetourPercent?: string }
    }>,
    reply: FastifyReply
  ): Promise<void> {
    const { planId } = request.params
    const { maxDetourKm = '20', maxDetourPercent = '15' } = request.query
    const userId = request.user!.uid

    // Verify plan ownership
    const plan = await this.plannerRepository.findById(planId)
    if (!plan?.userId || plan.userId !== userId) {
      return reply.status(404).send({ error: 'Plan not found' })
    }

    // Get all stages
    const stages = await this.plannerRepository.findByPlanId(planId)

    // Get POIs from AI cache or nearby search (Phase 2 integration)
    const pois: Array<{ id: string; name: string; category: string; lat: number; lng: number }> = []

    const recommendations = await routeOptimizationService.suggestDetours(
      stages,
      pois,
      Number(maxDetourKm),
      Number(maxDetourPercent)
    )

    reply.send({ recommendations })
  }
}
